/**
 * IndexedDB cache for session-replay chunk bytes.
 *
 * Chunks on the server are immutable (same s3Key always serves the same
 * bytes). We cache the gzipped bytes locally keyed by (sessionId, chunkIndex)
 * and treat the server-issued presigned-URL expiry as the cache TTL — when
 * the URL would no longer work, the cached bytes expire too. Sweep-on-mount
 * keeps the DB from growing unboundedly.
 *
 * No automatic LRU or size cap in v1 — chunks are small (~10–60 KB) and the
 * 6-hour URL expiry bounds cache growth naturally. The browser's own
 * quota-pressure eviction is the backstop.
 */

import Dexie, {type Table} from 'dexie';

interface ChunkRow {
	sessionId: string;
	chunkIndex: number;
	bytes: Uint8Array;
	expiresAt: number;
}

class ChunkCacheDB extends Dexie {
	chunks!: Table<ChunkRow, [string, number]>;

	constructor() {
		super('SessionReplayChunks');
		this.version(1).stores({
			// Compound primary key + indexes used by sweep/evict queries.
			chunks: '[sessionId+chunkIndex], sessionId, expiresAt',
		});
	}
}

let _db: ChunkCacheDB | null = null;

function getDB(): ChunkCacheDB {
	if (!_db) _db = new ChunkCacheDB();
	return _db;
}

/**
 * Return cached bytes for a chunk, or null if absent or expired.
 * Expired rows are deleted lazily on read.
 */
export async function getCachedChunk(sessionId: string, chunkIndex: number): Promise<Uint8Array | null> {
	try {
		const row = await getDB().chunks.get([sessionId, chunkIndex]);
		if (!row) return null;
		if (row.expiresAt <= Date.now()) {
			// Fire and forget — don't block the miss path on the delete
			getDB().chunks.delete([sessionId, chunkIndex]).catch(() => {});
			return null;
		}
		return row.bytes;
	}
	catch (err) {
		console.warn('chunkCache: read failed', err);
		return null;
	}
}

/**
 * Store a chunk's bytes. If `expiresAt` is missing or already in the past,
 * skip the write — no point caching something we'd immediately evict.
 */
export async function putCachedChunk(
	sessionId: string,
	chunkIndex: number,
	bytes: Uint8Array,
	expiresAt: number | undefined,
): Promise<void> {
	if (!expiresAt || expiresAt <= Date.now()) return;
	try {
		await getDB().chunks.put({sessionId, chunkIndex, bytes, expiresAt});
	}
	catch (err) {
		console.warn('chunkCache: write failed', err);
	}
}

/**
 * Delete every cached chunk for a session. Called when the server tells us
 * the session no longer exists (GET /replay/sessions/:id → 404).
 */
export async function evictSession(sessionId: string): Promise<void> {
	try {
		await getDB().chunks.where('sessionId').equals(sessionId).delete();
	}
	catch (err) {
		console.warn('chunkCache: evictSession failed', err);
	}
}

/**
 * Delete all chunks whose expiry has passed. Cheap when the DB is small;
 * run on each viewer mount so abandoned sessions don't linger forever.
 */
export async function sweepExpired(): Promise<void> {
	try {
		await getDB().chunks.where('expiresAt').belowOrEqual(Date.now()).delete();
	}
	catch (err) {
		console.warn('chunkCache: sweepExpired failed', err);
	}
}
